/**
 * MAHI — sign in / sign up, unified into one screen.
 *
 * There is no separate "Create account" page: the shopper only ever types a
 * phone number and a code. The backend decides on /otp/verify whether this
 * is a new or returning account and tells us via `is_new_user` — new users
 * go on to the one-screen Welcome step, returning users land on Home.
 *
 * Endpoints:
 *   POST /api/mira/auth/otp/send    { phone }
 *   POST /api/mira/auth/otp/verify  { phone, code } → { token, user, is_new_user }
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mira, { errMsg, trackEvent } from '../api';
import { useMiraAuth } from '../auth';
import MiraRobot from '../components/MiraRobot';
import MiraShell from '../components/MiraShell';

const RESEND_COOLDOWN_S = 30;

export default function MiraAuth() {
  const navigate = useNavigate();
  const { refresh } = useMiraAuth();
  const [stage, setStage] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const verifyingRef = useRef(false);

  useEffect(() => {
    document.title = 'MAHI — Sign in';
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Web OTP API — autofills the code from an incoming SMS on supported
  // Android/Chrome, no typing required.
  useEffect(() => {
    if (stage !== 'otp' || !('OTPCredential' in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((otp) => { if (otp?.code) setCode(otp.code.replace(/\D/g, '').slice(0, 6)); })
      .catch(() => {});
    return () => ac.abort();
  }, [stage]);

  const sendCode = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return toast.error('Enter a valid mobile number');
    setBusy(true);
    try {
      await mira.post('/auth/otp/send', { phone: digits });
      setStage('otp');
      setCooldown(RESEND_COOLDOWN_S);
      trackEvent('otp_sent');
      toast.success('Code sent');
    } catch (e) { toast.error(errMsg(e, 'Could not send code')); }
    finally { setBusy(false); }
  };

  const verify = async (candidateCode) => {
    const otp = (candidateCode ?? code).trim();
    if (otp.length < 4) return toast.error('Enter the code');
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setBusy(true);
    try {
      const { data } = await mira.post('/auth/otp/verify', {
        phone: phone.replace(/\D/g, ''), code: otp,
      });
      localStorage.setItem('mira_token', data.token);
      trackEvent(data.is_new_user ? 'signup_completed' : 'login_completed');
      await refresh();
      navigate(data.is_new_user ? '/mira/welcome' : '/mira/home', { replace: true });
    } catch (e) {
      toast.error(errMsg(e, 'Invalid or expired code'));
    } finally {
      verifyingRef.current = false;
      setBusy(false);
    }
  };

  // Auto-submit once 6 digits are present (typed or autofilled).
  useEffect(() => {
    if (stage === 'otp' && code.length === 6 && !busy) verify(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <MiraShell>
      <div className="min-h-screen flex items-center justify-center px-5 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <div className="flex justify-center mb-5"><MiraRobot size={96} /></div>
            <div className="text-[10px] tracking-[0.32em] text-slate-400 font-semibold">MAHI</div>
            <h1 className="mt-2 text-3xl font-semibold text-slate-900 tracking-tight">
              {stage === 'phone' ? 'Welcome' : 'Enter the code'}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {stage === 'phone' ? 'Your mobile number is all you need.' : `We texted a code to ${phone}`}
            </p>
          </div>

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)] p-7">
            {stage === 'phone' ? (
              <>
                <label className="block text-xs font-medium text-slate-500 mb-2">Mobile number</label>
                <div className="relative mb-5">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">+91</span>
                  <input
                    data-testid="mira-phone-input" type="tel" inputMode="numeric" autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                    onKeyDown={(e) => e.key === 'Enter' && sendCode()}
                    className="w-full rounded-xl border border-slate-200 pl-12 pr-4 py-3.5 text-lg tracking-wide focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
                    placeholder="98765 43210"
                    autoComplete="tel"
                  />
                </div>

                <button
                  data-testid="mira-send-otp-btn"
                  onClick={sendCode} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4" /></>}
                </button>
              </>
            ) : (
              <>
                <label className="block text-xs font-medium text-slate-500 mb-2">6-digit code</label>
                <input
                  data-testid="mira-otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full text-center tracking-[0.4em] text-2xl font-semibold rounded-xl border border-slate-200 px-4 py-3.5 mb-5 focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
                  placeholder="••••••"
                  inputMode="numeric" autoFocus autoComplete="one-time-code"
                />

                <button
                  data-testid="mira-verify-otp-btn"
                  onClick={() => verify()} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & continue'}
                </button>

                <div className="flex items-center justify-between mt-4">
                  <button
                    data-testid="mira-change-number-btn"
                    onClick={() => { setStage('phone'); setCode(''); }}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >Change number</button>
                  <button
                    data-testid="mira-resend-otp-btn"
                    onClick={sendCode} disabled={cooldown > 0 || busy}
                    className="text-xs text-slate-500 hover:text-slate-700 disabled:text-slate-300"
                  >{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}</button>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            By continuing you agree to MAHI&apos;s terms. A NanoKart product.
          </p>
        </motion.div>
      </div>
    </MiraShell>
  );
}
